import { KeyboardEvent, useEffect, useRef, useState } from 'react'
import { ArrowLeft, Mail, Send } from 'lucide-react'
import { EmptyState, IconButton, Spinner } from 'components/ds'
import { EmailIframe } from '../../agentic-hub/components/EmailIframe'
import { fetchSessionThreadEmails, sendSessionEmail, SessionEmailThread, SessionThreadEmail } from '../services/dispatchHelpApi'

const OUTBOUND = new Set(['USER', 'GENERAL_AGENT', 'INITIAL_EMAIL'])

let localSeq = 0

function senderLabel(e: SessionThreadEmail): string {
    const f = e.from?.[0]
    return f?.name || f?.email || (OUTBOUND.has(e.sentBy ?? '') ? 'You' : 'Broker')
}

function when(ms: number | null): string {
    if (!ms) return ''
    return new Date(ms).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
}

/** The inbox for one broker thread: renders the messages and lets the operator reply AS the owner. */
export function EmailThreadViewer({ tabId, thread, onBack }: { tabId: string; thread: SessionEmailThread; onBack: () => void }) {
    const [emails, setEmails] = useState<SessionThreadEmail[]>([])
    const [loading, setLoading] = useState(true)
    const [text, setText] = useState('')
    const [sending, setSending] = useState(false)
    const bottomRef = useRef<HTMLDivElement>(null)
    // Mirror of `emails` so the post-send poll reads the current pending rows without a stale closure.
    const emailsRef = useRef<SessionThreadEmail[]>([])
    useEffect(() => {
        emailsRef.current = emails
    }, [emails])
    const pollRef = useRef<ReturnType<typeof setTimeout>>()

    // Refetch + merge: keep an optimistic just-sent bubble until Nylas indexes it (a server row with the
    // same body appears) so it never vanishes or shows twice. Returns how many optimistic rows are still
    // un-indexed, so the caller can stop polling the moment the send lands.
    async function load(): Promise<number> {
        try {
            const rows = await fetchSessionThreadEmails(tabId, thread.emailThreadId)
            const serverBodies = new Set(rows.map((r) => (r.cleanedBody ?? r.body ?? '').trim()))
            const stillPending = emailsRef.current.filter((e) => e.messageId.startsWith('local-') && !serverBodies.has((e.cleanedBody ?? '').trim()))
            setEmails([...rows, ...stillPending])
            return stillPending.length
        } finally {
            setLoading(false)
        }
    }

    // Poll ONLY while a just-sent email is un-indexed — stop as soon as it lands, capped at 4 tries.
    function schedulePoll(attempt: number) {
        pollRef.current = setTimeout(async () => {
            const pending = await load()
            if (pending > 0 && attempt < 3) schedulePoll(attempt + 1)
        }, 2500)
    }

    useEffect(() => {
        setLoading(true)
        void load()
        return () => {
            if (pollRef.current) clearTimeout(pollRef.current)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tabId, thread.emailThreadId])

    useEffect(() => {
        bottomRef.current?.scrollIntoView({ block: 'end' })
    }, [emails.length])

    async function onSend() {
        const trimmed = text.trim()
        if (!trimmed || sending) return
        setSending(true)
        setText('')
        const optimistic: SessionThreadEmail = {
            messageId: `local-${++localSeq}`,
            subject: null,
            from: [{ email: 'you', name: 'You' }],
            to: thread.brokerEmail ? [{ email: thread.brokerEmail, name: thread.brokerName ?? undefined }] : [],
            body: null,
            cleanedBody: trimmed,
            emailDate: Date.now(),
            sentBy: 'USER',
        }
        setEmails((prev) => [...prev, optimistic])
        try {
            await sendSessionEmail(tabId, thread.id, trimmed)
            // Nylas indexes shortly after — poll until the sent row lands, then stop (no blind refetches).
            if (pollRef.current) clearTimeout(pollRef.current)
            schedulePoll(0)
        } catch {
            setEmails((prev) => prev.filter((e) => e.messageId !== optimistic.messageId))
            setText(trimmed)
        } finally {
            setSending(false)
        }
    }

    function onKeyDown(e: KeyboardEvent<HTMLTextAreaElement>) {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault()
            void onSend()
        }
    }

    return (
        <div className="flex h-full flex-col">
            <div className="flex items-center gap-2 border-b border-slate-200 px-3 py-2.5 dark:border-slate-700">
                <IconButton variant="ghost" size="sm" icon={<ArrowLeft className="h-4 w-4" />} onClick={onBack} aria-label="Back" />
                <div className="min-w-0">
                    <div className="truncate text-sm font-semibold text-slate-900 dark:text-slate-100">
                        {thread.brokerName || thread.brokerEmail || 'Broker'}
                    </div>
                    {thread.brokerEmail && <div className="truncate text-xs text-slate-400">{thread.brokerEmail}</div>}
                </div>
            </div>

            <div className="flex-1 space-y-3 overflow-y-auto px-3 py-3">
                {loading ? (
                    <div className="flex h-full items-center justify-center">
                        <Spinner />
                    </div>
                ) : emails.length === 0 ? (
                    <EmptyState icon={<Mail />} title="No messages yet" compact />
                ) : (
                    emails.map((e) => {
                        const out = OUTBOUND.has(e.sentBy ?? '')
                        return (
                            <div key={e.messageId} className={`flex ${out ? 'justify-end' : 'justify-start'}`}>
                                <div
                                    className={`max-w-[85%] rounded-xl px-3 py-2 text-sm ring-1 ${
                                        out
                                            ? 'bg-blue-50 text-slate-800 ring-blue-200 dark:bg-blue-500/10 dark:text-slate-100 dark:ring-blue-500/30'
                                            : 'bg-white text-slate-800 ring-slate-200 dark:bg-slate-800 dark:text-slate-100 dark:ring-slate-700'
                                    }`}
                                >
                                    <div className="mb-1 flex items-center justify-between gap-3 text-[11px] text-slate-400">
                                        <span className="font-medium text-slate-500 dark:text-slate-300">{senderLabel(e)}</span>
                                        <span>{e.messageId.startsWith('local-') ? 'Sending…' : when(e.emailDate)}</span>
                                    </div>
                                    {e.body ? <EmailIframe html={e.body} /> : <div className="whitespace-pre-wrap">{e.cleanedBody ?? ''}</div>}
                                </div>
                            </div>
                        )
                    })
                )}
                <div ref={bottomRef} />
            </div>

            <div className="border-t border-slate-200 p-2 dark:border-slate-700">
                <div className="flex items-end gap-2">
                    <textarea
                        value={text}
                        onChange={(e) => setText(e.target.value)}
                        onKeyDown={onKeyDown}
                        rows={2}
                        placeholder="Write a reply to the broker (sent as the driver)…"
                        className="max-h-40 flex-1 resize-none rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 outline-none focus:border-blue-400 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
                    />
                    <button
                        type="button"
                        onClick={onSend}
                        disabled={!text.trim() || sending}
                        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-blue-500 text-white transition-colors hover:bg-blue-600 disabled:opacity-40"
                    >
                        <Send className="h-4 w-4" />
                    </button>
                </div>
                <div className="mt-1 px-1 text-[11px] text-slate-400">Enter to send · Shift+Enter for a new line</div>
            </div>
        </div>
    )
}
